import React, { useRef } from 'react';
import classNames from 'classnames';
import { useStore, useStoreMap } from 'effector-react';
import { useDrag, useDrop } from 'react-dnd';
import { DndType, type DragItem, type ECategoryItem } from '@types';
import { showInputModal } from '@components/InputModal';
import {
  $categoryMap,
  $categoryMapApi,
  $curSubCategoryId,
} from '@stores/dataStores';
import { $panelApi } from '@stores/panelStores';
import { showConfirmModal } from '@components/ConfirmModal';
import MoreButton from '../MoreButton';
import { selectSubCategory } from '../actions/selectActions';
import { sortSubCategory } from '../actions/sortActions';
import { moveSceneToSubCategory } from '../actions/moveToActions';
import { deleteSubCategory } from '../actions/deleteActions';
import { sortSubCategoryScenes } from '../actions/sortScenesActions';
import { showScenesCopyModal } from '../ScenesCopyModal';

interface Props {
  id: number;
  index: number;
}

function SubCategoryItem({ id, index }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const isActive = useStore($curSubCategoryId) === id;
  const { name } = useStoreMap({
    store: $categoryMap,
    keys: [id],
    fn: (state, [_id]) => state.get(_id) || ({} as ECategoryItem),
  });

  const [{ isDragging }, drag] = useDrag({
    type: DndType.subCategory,
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isDropTarget }, drop] = useDrop({
    accept: [DndType.scene, DndType.subCategory],
    canDrop: (item: DragItem, monitor) => {
      if (isActive) return false;
      return monitor.getItemType() === DndType.scene;
    },
    collect: (monitor) => ({
      isDropTarget: monitor.isOver() && monitor.canDrop(),
    }),
    hover: (item: DragItem, monitor) => {
      if (!ref.current) return;
      if (monitor.getItemType() !== DndType.subCategory) return;
      const fromIndex = item.index;
      const toIndex = index;
      if (fromIndex === toIndex) return;
      const rect = ref.current.getBoundingClientRect();
      const hoverMiddleX = (rect.right - rect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientX = clientOffset!.x - rect.left;
      if (fromIndex < toIndex && hoverClientX < hoverMiddleX) return;
      if (fromIndex > toIndex && hoverClientX > hoverMiddleX) return;
      sortSubCategory({ fromIndex, toIndex });
      item.index = toIndex;
    },
    drop: (item: DragItem) => {
      moveSceneToSubCategory({ id: item.id, targetId: id });
    },
  });

  drop(drag(ref));

  const handleDelete = async () => {
    $panelApi.dismiss();
    const { confirmed } = await showConfirmModal({
      title: '确认删除分组',
      content: '分组下所有场景也都将会被删除，确认删除？',
    });
    if (confirmed) {
      deleteSubCategory({ subId: id });
    }
  };

  const handleRename = async () => {
    const { value } = await showInputModal({
      title: '分组重命名',
      placeholder: '请输入分组名称',
      initialValue: name,
      maxLength: 50,
    });
    if (value) {
      $categoryMapApi.update((draft) => {
        draft.get(id)!.name = value;
      });
    }
  };

  return (
    <div
      ref={ref}
      className={classNames(
        'ml-20 flex max-w-[125px] cursor-pointer items-center',
        {
          'text-black-gray hover:text-white': !isActive && !isDropTarget,
          'opacity-0': isDragging,
        }
      )}
      onClick={() => isActive || selectSubCategory(id)}
    >
      <div className="truncate">{name}</div>
      <MoreButton
        items={['重新命名', '删除分组', '一键排序', '跨作品复制'] as const}
        onSelect={async (val) => {
          if (val === '删除分组') {
            await handleDelete();
          } else if (val === '重新命名') {
            await handleRename();
          } else if (val === '一键排序') {
            sortSubCategoryScenes(id);
          } else if (val === '跨作品复制') {
            showScenesCopyModal({ level: '二级分组', subCategoryId: id });
          }
        }}
        className="ml-5 flex-shrink-0"
      />
    </div>
  );
}

export default SubCategoryItem;
